import { useAuth } from "@/core/components/auth/store/auth";
import { useFilters } from "@/_old/features/filters";
import { getSubscribersApi } from "@/_old/features/subscribers/adapters/get-subscribers";
import { cookieManager } from "@/_old/utils/cookie-manager";
import {
  createContext,
  FunctionComponent,
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useSearchParams } from "react-router-dom";
import { SubscribersContextProps } from "./subscribers-context.types";

const SubscribersContext = createContext<SubscribersContextProps>(
  {} as SubscribersContextProps,
);

export const SubscribersProvider: FunctionComponent<PropsWithChildren<{}>> = ({
  children,
}) => {
  const { groupId, groupName } = useFilters();
  const [search, setSearch] = useState("");
  const [subscribersSelected, setSubscribersSelected] = useState<
    { id: number; email: string }[]
  >([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedEmailSubscribers, setSelectedEmailSubscribers] = useState<
    { id: number; email: string }[]
  >([]);
  const [selectedUserIds, setSelectedUserIds] = useState<
    { id: number; email: string }[]
  >([]);
  const [data, setData] = useState<any[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(false);

  const [linesPerPage, setLinesPerPage] = useState<number>(10);
  const [activePage, setActivePage] = useState(
    searchParams.get("page") ? Number(searchParams.get("page")) : 1,
  );

  const {
    userInfo: { user, profile },
  } = useAuth();

  const page = Number(searchParams.get("page")) || 1;

  const subscribersFilters = useMemo(() => {
    return {
      limit: linesPerPage,
      page,
      league: Number(user.leagues_id[0]),
      search,
      groupId,
    };
  }, [linesPerPage, page, user.leagues_id, search, groupId]);

  useEffect(() => {
    const actualDate = new Date();
    const expirationDate = new Date(
      new Date(actualDate).setMonth(actualDate.getMonth() + 1),
    );

    cookieManager.set({
      cookieName: "linesPerPage",
      value: linesPerPage,
      globally: true,
      expirationDate,
    });
  }, [linesPerPage]);

  const handleChangeLinesPerPage = (value: number) => {
    setLinesPerPage(value);
  };

  useEffect(() => {
    refetchSubscribers();
  }, [subscribersFilters, profile.role]);

  const handleList = (page: number) => {
    setActivePage(page);
    setSearchParams({ page: String(page) });
  };

  const handleSelectSubscribers = (
    data: { id: number; email: string; accessGroups?: number[] }[],
  ) => {
    setSubscribersSelected(data);
  };

  const refetchSubscribers = async () => {
    setIsLoading(true);
    if (!profile.permissions.includes("read-company-groups") && !groupId) {
      setData([]);
      setTotal(0);
      return;
    }

    try {
      const response = await getSubscribersApi(subscribersFilters);
      setData(response.data);
      setTotal(response.total);
    } catch (error) {
      console.error("Error refetching subscribers:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangePage = async (page: number) => {
    handleList(page);
  };

  return (
    <SubscribersContext.Provider
      value={{
        activePage,
        setActivePage,
        search,
        setSearch,
        data,
        total,
        handleChangePage,
        groupId,
        page,
        handleChangeLinesPerPage,
        linesPerPage,
        subscribersSelected,
        setSubscribersSelected,
        handleSelectSubscribers,
        selectedEmailSubscribers,
        setSelectedEmailSubscribers,
        selectedUserIds,
        setSelectedUserIds,
        refetchSubscribers,
        isLoading,
      }}
    >
      {children}
    </SubscribersContext.Provider>
  );
};

export const useSubscribers = () => useContext(SubscribersContext);
